/*
 * Page metadata
 */

import { Helmet } from 'react-helmet-async';
import PropTypes from 'prop-types';
import React from 'react';
import StripTags from 'striptags';

import { getLocation } from '$utilities/location';
import { useLocale } from '$contexts/locale.jsx';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @component Meta
 * @props (see propTypes)
 */
const Meta = ({
    description,
    image,
    keywords,
    page = {},
    publicationDate,
    title,
    type
}) => {
    const { options: localeOptions, formatURI, translate: t } = useLocale();
    const location = getLocation();

    description = description || page?.seoMetadata?.description;
    image = `${image || page?.seoMetadata?.image?.src || `${location.origin}/images/default-x2.jpg`}?w=1200&fm=jpg`;
    keywords = keywords || page?.seoMetadata?.keywords;
    publicationDate = publicationDate || page.publicationDate;
    title = title || page.seoTitle || page.title || t('Strava | Business');
    type = type || 'WebPage';

    const hreflang = [
        <link
            href={formatURI(location.pathname, null)}
            hrefLang='x-default'
            key='xdefault'
            rel='alternate' />
    ].concat(localeOptions.map((option, i) => {
        return (
            <link
                href={formatURI(location.pathname, option.code)}
                hrefLang={option.code}
                key={i}
                rel='alternate' />
        );
    }));

    const schema = {
        '@context': 'http://schema.org',
        '@type': type,
        'headline': title || '',
        'url': location.href,
        'thumbnailUrl': image || '',
        'datePublished': publicationDate || '',
        'keywords': keywords || ''
    };

    return (
        <Helmet>
            <meta
                content='Strava Business'
                name='site_name' />
            {title && <title>
                {title}
            </title>}
            {description && <meta
                content={StripTags(description)}
                name='description' />}
            {keywords && <meta
                content={keywords}
                name='keywords' />}
            {hreflang}
            {description && <meta
                content={StripTags(description)}
                property='og:description' />}
            <meta
                content={image}
                property='og:image' />
            {title && <meta
                content={title}
                property='og:title' />}
            {<meta
                content={location.href}
                property='og:url' />}
            <meta
                content='website'
                property='og:type' />
            <meta
                content='summary_large_image'
                name='twitter:card' />
            {description && <meta
                content={StripTags(description)}
                name='twitter:description' />}
            <meta
                content={image}
                property='twitter:image:src' />
            {title && <meta
                content={title}
                property='twitter:title' />}
            <link
                href={location.href}
                rel='canonical' />
            <script type='application/ld+json'>
                {JSON.stringify(schema)}
            </script>
        </Helmet>
    );

};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

Meta.propTypes = {
    description: PropTypes.string,
    image: PropTypes.string,
    keywords: PropTypes.string,
    page: PropTypes.object,
    publicationDate: PropTypes.string,
    title: PropTypes.string,
    type: PropTypes.string
};

export default Meta;
